/**
 * Time Tracking Service
 *
 * Core service for workshop time tracking operations.
 * Handles starting/stopping timers with proper atomicity.
 *
 * Key behaviors:
 * - startWorkOrderTimer: Auto-stops any existing timer before starting new one
 * - stopWorkOrderTimer: Creates time entry and deletes active timer
 * - All operations use Firestore transactions for consistency
 */

import { runTransaction, doc, collection, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import type {
  ActiveTimer,
  ActiveTimerDocument,
  TimeEntry,
  StartTimerInput,
  StartTimerResult,
  StopTimerResult,
} from '../types/timeEntry';
import { timestampToIso, computeDurationMinutes, computeTotalCost } from '../types/timeEntry';
import type { WorkOrderStatus } from '../types';

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined)
 */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined)) as Partial<T>;
}

/** Statuses that should auto-transition to IN_PROGRESS when timer starts */
const AUTO_PROGRESS_STATUSES: WorkOrderStatus[] = ['NIEUW', 'PLANNED'];

/**
 * Start a timer for a work order
 *
 * Behavior:
 * 1. Check if activeTimers/{mechanicId} exists
 * 2. If it exists and points to a different workOrder:
 *    - Auto-stop it: Create a TimeEntry in the old work order
 *    - Delete old activeTimers/{mechanicId}
 * 3. Create/overwrite activeTimers/{mechanicId} for the new workOrder
 * 4. If work order status is NIEUW or PLANNED, auto-update to IN_PROGRESS
 *
 * Uses a Firestore transaction to ensure atomicity.
 */
export async function startWorkOrderTimer(
  teamId: string,
  workOrderId: string,
  mechanicId: string,
  mechanicName: string | undefined,
  input: StartTimerInput
): Promise<StartTimerResult> {
  logger.info(
    `[TimeTracking] Starting timer for work order ${workOrderId} by mechanic ${mechanicId}`
  );

  const activeTimerRef = doc(db, 'teams', teamId, 'activeTimers', mechanicId);

  const workOrderRef = doc(db, 'teams', teamId, 'workOrders', workOrderId);

  const result = await runTransaction(db, async (transaction) => {
    // ═══════════════════════════════════════════════════════════════
    // PHASE 1: ALL READS FIRST (Firestore requires reads before writes)
    // ═══════════════════════════════════════════════════════════════

    // Read 1: Check for existing active timer
    const existingTimerSnap = await transaction.get(activeTimerRef);

    // Read 2: Get work order for status check
    const workOrderSnap = await transaction.get(workOrderRef);

    // ═══════════════════════════════════════════════════════════════
    // PHASE 2: COMPUTE ALL DERIVED VALUES
    // ═══════════════════════════════════════════════════════════════

    const now = Timestamp.now();
    let autoStoppedPrevious: StartTimerResult['autoStoppedPrevious'] = undefined;
    let autoStopTimeEntryRef: ReturnType<typeof doc> | null = null;
    let autoStopTimeEntryData: Record<string, unknown> | null = null;

    // Check if we need to auto-stop a previous timer
    if (existingTimerSnap.exists()) {
      const existingTimer = existingTimerSnap.data() as ActiveTimerDocument;

      if (existingTimer.workOrderId !== workOrderId) {
        logger.info(
          `[TimeTracking] Auto-stopping previous timer on work order ${existingTimer.workOrderId}`
        );

        const durationMinutes = computeDurationMinutes(existingTimer.startedAt, now);
        const totalCost = computeTotalCost(durationMinutes, existingTimer.hourlyCostSnapshot);
        const totalSell = existingTimer.hourlySellSnapshot
          ? computeTotalCost(durationMinutes, existingTimer.hourlySellSnapshot)
          : undefined;

        // Prepare time entry for the OLD work order
        const oldTimeEntriesCol = collection(
          db,
          'teams',
          teamId,
          'workOrders',
          existingTimer.workOrderId,
          'timeEntries'
        );
        autoStopTimeEntryRef = doc(oldTimeEntriesCol);

        autoStopTimeEntryData = stripUndefined({
          id: autoStopTimeEntryRef.id,
          workOrderId: existingTimer.workOrderId,
          mechanicId: existingTimer.mechanicId,
          mechanicName: existingTimer.mechanicName,
          startedAt: existingTimer.startedAt,
          endedAt: now,
          durationMinutes,
          hourlyCostSnapshot: existingTimer.hourlyCostSnapshot,
          hourlySellSnapshot: existingTimer.hourlySellSnapshot,
          totalCost,
          totalSell,
          source: 'TIMER' as const,
          createdAt: now,
          updatedAt: now,
          createdBy: mechanicId,
        });

        autoStoppedPrevious = {
          workOrderId: existingTimer.workOrderId,
          timeEntryId: autoStopTimeEntryRef.id,
          durationMinutes,
        };
      } else {
        logger.info(`[TimeTracking] Timer already running for same work order, updating`);
      }
    }

    // Prepare new timer data
    const newTimerData = stripUndefined({
      mechanicId,
      mechanicName,
      workOrderId: input.workOrderId,
      workOrderTitle: input.workOrderTitle,
      vehicleId: input.vehicleId,
      vehiclePlate: input.vehiclePlate,
      startedAt: now,
      hourlyCostSnapshot: input.hourlyCostSnapshot,
      hourlySellSnapshot: input.hourlySellSnapshot,
      updatedAt: now,
    }) as ActiveTimerDocument;

    // Check if work order status should be updated
    let statusUpdated = false;
    if (workOrderSnap.exists()) {
      const workOrderData = workOrderSnap.data();
      const currentStatus = workOrderData?.status as WorkOrderStatus | undefined;
      if (currentStatus && AUTO_PROGRESS_STATUSES.includes(currentStatus)) {
        statusUpdated = true;
      }
    }

    // ═══════════════════════════════════════════════════════════════
    // PHASE 3: ALL WRITES LAST
    // ═══════════════════════════════════════════════════════════════

    // Write 1: Create time entry for auto-stopped timer (if applicable)
    if (autoStopTimeEntryRef && autoStopTimeEntryData) {
      transaction.set(autoStopTimeEntryRef, autoStopTimeEntryData);
    }

    // Write 2: Create/overwrite active timer
    transaction.set(activeTimerRef, newTimerData);

    // Write 3: Update work order status (if applicable)
    if (statusUpdated) {
      logger.info(`[TimeTracking] Auto-updating work order status to IN_PROGRESS`);
      transaction.update(workOrderRef, {
        status: 'IN_PROGRESS',
        updatedAt: serverTimestamp(),
      });
    }

    // Return result
    const activeTimer: ActiveTimer = {
      ...newTimerData,
      startedAt: timestampToIso(now),
      updatedAt: timestampToIso(now),
    };

    return {
      activeTimer,
      autoStoppedPrevious,
      statusUpdated,
    };
  });

  logger.info(
    `[TimeTracking] Timer started successfully${
      result.autoStoppedPrevious
        ? ` (auto-stopped previous: ${result.autoStoppedPrevious.durationMinutes}min)`
        : ''
    }`
  );

  return result;
}

/**
 * Stop a timer for a mechanic
 *
 * Behavior:
 * 1. Read activeTimers/{mechanicId}
 * 2. If none exists, return null (no-op)
 * 3. Create a TimeEntry under that active timer's workOrder
 * 4. Delete activeTimers/{mechanicId}
 *
 * Uses a Firestore transaction to ensure atomicity.
 */
export async function stopWorkOrderTimer(
  teamId: string,
  mechanicId: string
): Promise<StopTimerResult | null> {
  logger.info(`[TimeTracking] Stopping timer for mechanic ${mechanicId}`);

  const activeTimerRef = doc(db, 'teams', teamId, 'activeTimers', mechanicId);

  const result = await runTransaction(db, async (transaction) => {
    // 1. Read active timer
    const timerSnap = await transaction.get(activeTimerRef);

    if (!timerSnap.exists()) {
      logger.info(`[TimeTracking] No active timer found for mechanic ${mechanicId}`);
      return null;
    }

    const timer = timerSnap.data() as ActiveTimerDocument;

    // 2. Validate work order exists (graceful handling)
    if (!timer.workOrderId) {
      logger.warn(`[TimeTracking] Active timer has no workOrderId, deleting orphan timer`);
      transaction.delete(activeTimerRef);
      return null;
    }

    // 3. Create time entry
    const now = Timestamp.now();
    const durationMinutes = computeDurationMinutes(timer.startedAt, now);
    const totalCost = computeTotalCost(durationMinutes, timer.hourlyCostSnapshot);
    const totalSell = timer.hourlySellSnapshot
      ? computeTotalCost(durationMinutes, timer.hourlySellSnapshot)
      : undefined;

    const timeEntriesCol = collection(
      db,
      'teams',
      teamId,
      'workOrders',
      timer.workOrderId,
      'timeEntries'
    );
    const newTimeEntryRef = doc(timeEntriesCol);

    const timeEntryData = stripUndefined({
      id: newTimeEntryRef.id,
      workOrderId: timer.workOrderId,
      mechanicId: timer.mechanicId,
      mechanicName: timer.mechanicName,
      startedAt: timer.startedAt,
      endedAt: now,
      durationMinutes,
      hourlyCostSnapshot: timer.hourlyCostSnapshot,
      hourlySellSnapshot: timer.hourlySellSnapshot,
      totalCost,
      totalSell,
      source: 'TIMER' as const,
      createdAt: now,
      updatedAt: now,
      createdBy: mechanicId,
    });

    transaction.set(newTimeEntryRef, timeEntryData);

    // 4. Delete active timer
    transaction.delete(activeTimerRef);

    // Return result
    const timeEntry: TimeEntry = {
      id: newTimeEntryRef.id,
      workOrderId: timer.workOrderId,
      mechanicId: timer.mechanicId,
      mechanicName: timer.mechanicName,
      durationMinutes,
      hourlyCostSnapshot: timer.hourlyCostSnapshot,
      hourlySellSnapshot: timer.hourlySellSnapshot,
      totalCost,
      totalSell,
      notes: undefined,
      source: 'TIMER',
      createdBy: mechanicId,
      startedAt: timestampToIso(timer.startedAt),
      endedAt: timestampToIso(now),
      createdAt: timestampToIso(now),
      updatedAt: timestampToIso(now),
    };

    return {
      timeEntry,
      durationMinutes,
    };
  });

  if (result) {
    logger.info(
      `[TimeTracking] Timer stopped successfully: ${result.durationMinutes} minutes recorded`
    );
  }

  return result;
}

/**
 * Discard a timer without creating a time entry
 * Useful for canceling accidental timer starts
 */
export async function discardWorkOrderTimer(teamId: string, mechanicId: string): Promise<boolean> {
  logger.info(`[TimeTracking] Discarding timer for mechanic ${mechanicId}`);

  const activeTimerRef = doc(db, 'teams', teamId, 'activeTimers', mechanicId);

  const result = await runTransaction(db, async (transaction) => {
    const timerSnap = await transaction.get(activeTimerRef);

    if (!timerSnap.exists()) {
      logger.info(`[TimeTracking] No active timer to discard`);
      return false;
    }

    transaction.delete(activeTimerRef);
    return true;
  });

  if (result) {
    logger.info(`[TimeTracking] Timer discarded successfully`);
  }

  return result;
}
